import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
 
import axios from 'axios';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Select,
  Tag,
  Card,
  Popconfirm,
  message,
  ConfigProvider,
  Row,
  Col,
  Upload,
  Image,
  Divider,
  Space,
  Typography,
  Collapse,
  List,
  Badge
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  LoadingOutlined,
  EyeOutlined,
  DeleteTwoTone,
  InfoCircleOutlined,
  CheckCircleOutlined,
  GiftOutlined
} from '@ant-design/icons';
import { API_BASE_URL } from '../components/l1';
const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;
const { Panel } = Collapse;

// ====================
// 1. API SERVICE
 

// Categories API
const categoryApi = {
  getAll: () => axios.get(`${API_BASE_URL}/categories`).then(res => res.data),
};

// Products API (for selecting items)
const productApi = {
  getAll: () => axios.get(`${API_BASE_URL}/products`).then(res => res.data),
};

// Combos API
const comboApi = {
  getAll: () => axios.get(`${API_BASE_URL}/combos`).then(res => res.data),
  create: (data) => axios.post(`${API_BASE_URL}/combos`, data).then(res => res.data),
  update: (id, data) => axios.put(`${API_BASE_URL}/combos/${id}`, data).then(res => res.data),
  delete: (id) => axios.delete(`${API_BASE_URL}/combos/${id}`).then(res => res.data),
};

 

// ====================
// 3. REACT QUERY HOOKS
// ====================
// Categories hook
const useCategories = () => {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => categoryApi.getAll(),
    select: (data) => data.data || [],
  });
};

// Products hook (for item selection)
const useProducts = () => {
  return useQuery({
    queryKey: ['products'],
    queryFn: () => productApi.getAll(),
    select: (data) => data.data || [],
  });
};

// Combos hooks
const useCombos = () => {
  return useQuery({
    queryKey: ['combos'],
    queryFn: () => comboApi.getAll(),
    select: (data) => data.data || [],
  });
};

const useCreateCombo = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => comboApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['combos'] });
      message.success('Combo created successfully');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to create combo');
    },
  });
};

const useUpdateCombo = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => comboApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['combos'] });
      message.success('Combo updated successfully');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to update combo');
    },
  });
};

const useDeleteCombo = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => comboApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['combos'] });
      message.success('Combo deleted successfully');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to delete combo');
    },
  });
};

// ====================
// 4. BRIEF PRICE ARRAY COMPONENT (FIXED)
// ====================
const BriefPriceArray = ({ value = [], onChange }) => {
  const [items, setItems] = useState(() => {
    if (value && Array.isArray(value)) {
      return value.map(item => {
        const entries = Object.entries(item);
        if (entries.length > 0) {
          const [key, val] = entries[0];
          return { key, value: val };
        }
        return { key: '', value: '' };
      });
    }
    return [{ key: '', value: '' }];
  });

  // Update parent only when needed
  const updateParent = (newItems) => {
    const filtered = newItems.filter(item => 
      item.key && item.key.trim() && item.value !== '' && item.value !== null
    );
    
    const formatted = filtered.map(item => ({
      [item.key.trim()]: Number(item.value)
    }));
    
    onChange(formatted);
  };

  const handleChange = (index, field, newValue) => {
    const newItems = [...items];
    
    if (field === 'value') {
      // Handle empty value or number
      newItems[index] = {
        ...newItems[index],
        value: newValue === null || newValue === '' ? '' : Number(newValue)
      };
    } else {
      newItems[index] = {
        ...newItems[index],
        key: newValue
      };
    }
    
    setItems(newItems);
    updateParent(newItems);
  };

  const addItem = () => {
    const newItems = [...items, { key: '', value: '' }];
    setItems(newItems);
  };

  const removeItem = (index) => {
    if (items.length === 1) {
      // Clear the only item
      setItems([{ key: '', value: '' }]);
      updateParent([{ key: '', value: '' }]);
    } else {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
      updateParent(newItems);
    }
  };

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            placeholder="Option name"
            value={item.key}
            onChange={(e) => handleChange(index, 'key', e.target.value)}
            className="flex-1"
          />
          <InputNumber
            placeholder="Price"
            value={item.value}
            onChange={(val) => handleChange(index, 'value', val)}
            min={0}
            className="w-32"
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeItem(index)}
          />
        </div>
      ))}
      <Button type="dashed" onClick={addItem} block>
        Add Price Option
      </Button>
    </div>
  );
};

// ====================
// 5. FEATURES ARRAY COMPONENT
// ====================
const FeaturesArray = ({ value = [], onChange }) => {
  const [features, setFeatures] = useState(value || []);

  const addFeature = () => {
    const newFeatures = [...features, ''];
    setFeatures(newFeatures);
    onChange(newFeatures);
  };

  const updateFeature = (index, newValue) => {
    const newFeatures = [...features];
    newFeatures[index] = newValue;
    setFeatures(newFeatures);
    onChange(newFeatures);
  };

  const removeFeature = (index) => {
    const newFeatures = features.filter((_, i) => i !== index);
    setFeatures(newFeatures);
    onChange(newFeatures);
  };

  return (
    <div className="space-y-2">
      {features.map((feature, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            placeholder="Enter feature (e.g., 12 Red Roses Bouquet)"
            value={feature}
            onChange={(e) => updateFeature(index, e.target.value)}
            className="flex-1"
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeFeature(index)}
          />
        </div>
      ))}
      <Button type="dashed" onClick={addFeature} block icon={<PlusOutlined />}>
        Add Feature
      </Button>
    </div>
  );
};

// ====================
// 6. PRODUCT SELECTOR COMPONENT
// ====================
const ProductSelector = ({ products = [], value = [], onChange }) => {
  const [selectedIds, setSelectedIds] = useState(value || []);

  const handleSelect = (id) => {
    let newSelectedIds;
    if (selectedIds.includes(id)) {
      newSelectedIds = selectedIds.filter(itemId => itemId !== id);
    } else {
      newSelectedIds = [...selectedIds, id];
    }
    setSelectedIds(newSelectedIds);
    onChange(newSelectedIds);
  };

  const removeProduct = (id) => {
    const newSelectedIds = selectedIds.filter(itemId => itemId !== id);
    setSelectedIds(newSelectedIds);
    onChange(newSelectedIds);
  };

  const selectedProducts = products.filter(product => 
    selectedIds.includes(product.id)
  );

  return (
    <div className="space-y-4">
      {/* Selected Products */}
      {selectedProducts.length > 0 && (
        <div>
          <div className="font-medium mb-2">Selected Products:</div>
          <div className="space-y-2">
            {selectedProducts.map(product => (
              <div key={product.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                <div className="flex items-center gap-2">
                  <Image
                    src={product.image}
                    alt={product.name}
                    width={40}
                    height={40}
                    className="rounded border object-cover"
                  />
                  <div>
                    <div className="font-medium text-sm">{product.name}</div>
                    <div className="text-xs text-gray-500">ID: {product.id}</div>
                  </div>
                </div>
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => removeProduct(product.id)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Products */}
      <div>
        <div className="font-medium mb-2">Available Products:</div>
        <div className="max-h-60 overflow-y-auto border rounded p-2">
          {products.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              No products available
            </div>
          ) : (
            <div className="space-y-2">
              {products.map(product => (
                <div
                  key={product.id}
                  className={`p-3 border rounded cursor-pointer transition-all ${
                    selectedIds.includes(product.id)
                      ? 'bg-blue-50 border-blue-300'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => handleSelect(product.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                      selectedIds.includes(product.id) 
                        ? 'bg-blue-500 border-blue-500' 
                        : 'border-gray-300'
                    }`}>
                      {selectedIds.includes(product.id) && (
                        <CheckCircleOutlined className="text-white text-xs" />
                      )}
                    </div>
                    <Image
                      src={product.image}
                      alt={product.name}
                      width={50}
                      height={50}
                      className="rounded border object-cover"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <div className="font-medium">{product.name}</div>
                        <Tag color="blue">ID: {product.id}</Tag>
                      </div>
                      <div className="text-sm text-gray-600 truncate">
                        {product.description}
                      </div>
                      <div className="flex gap-2 mt-1">
                        <Tag color="green">₹{product.price}</Tag>
                        <Tag color="orange">{product.category}</Tag>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="text-sm text-gray-500">
        Selected: {selectedIds.length} product(s)
      </div>
    </div>
  );
};

// ====================
// 7. COMBO FORM COMPONENT
// ====================
const ComboForm = ({ open, onClose, editingCombo, categories = [], products = [] }) => {
  const [form] = Form.useForm();
  const createMutation = useCreateCombo();
  const updateMutation = useUpdateCombo();
  const [loading, setLoading] = useState(false);
  const [mainImage, setMainImage] = useState('');
  const [additionalImages, setAdditionalImages] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  // Set form values when editing
  useEffect(() => {
    if (editingCombo && open) {
      form.setFieldsValue({
        ...editingCombo,
        category: editingCombo.category || '',
        brifPrize: editingCombo.brifPrize || [],
        features: editingCombo.features || [],
        discount: editingCombo.discount || 0,
        items: editingCombo.items || []
      });
      setMainImage(editingCombo.image || '');
      setAdditionalImages(editingCombo.images || []);
      setSelectedItems(editingCombo.items || []);
    } else {
      form.resetFields();
      setMainImage('');
      setAdditionalImages([]);
      setSelectedItems([]);
    }
  }, [editingCombo, open, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Process data
      const processedValues = {
        ...values,
        type: 'combo', // Always combo
        items: selectedItems, // Use selected items array
        images: additionalImages,
        image: mainImage,
        category: values.category,
        brifPrize: values.brifPrize || [],
        features: values.features || [],
        discount: values.discount || 0
      };

      if (editingCombo) {
        await updateMutation.mutateAsync({ 
          id: editingCombo.id || editingCombo._id, 
          data: processedValues 
        });
      } else {
        await createMutation.mutateAsync(processedValues);
      }
      
      onClose();
      form.resetFields();
      setMainImage('');
      setAdditionalImages([]);
      setSelectedItems([]);
    } catch (error) {
      // Error handled by mutation
    } finally {
      setLoading(false);
    }
  };

  const handleMainImageChange = (e) => {
    setMainImage(e.target.value);
  };

  const handleAddImage = () => {
    setAdditionalImages([...additionalImages, '']);
  };

  const handleImageChange = (index, value) => {
    const newImages = [...additionalImages];
    newImages[index] = value;
    setAdditionalImages(newImages);
  };

  const handleRemoveImage = (index) => {
    const newImages = additionalImages.filter((_, i) => i !== index);
    setAdditionalImages(newImages);
  };

  const handleItemsChange = (items) => {
    setSelectedItems(items);
    form.setFieldsValue({ items });
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <GiftOutlined className="text-purple-500" />
          {editingCombo ? 'Edit Combo' : 'Create New Combo'}
        </div>
      }
      open={open}
      onCancel={onClose}
      width={1000}
      style={{ top: 20 }}
      footer={null}
      className="combo-form-modal"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          type: 'combo',
          inStock: true,
          allowCustomMessage: true,
          messageOnCard: true,
          discount: 0,
          brifPrize: [],
          features: [],
          items: []
        }}
      >
        <div className="max-h-[70vh] overflow-y-auto pr-2">
          <Row gutter={16}>
            {/* Left Column */}
            <Col span={12}>
              <Form.Item
                name="id"
                label="Combo ID"
                rules={[
                  { required: true, message: 'Please enter combo ID' },
                  { type: 'number', min: 1, message: 'ID must be positive number' }
                ]}
              >
                <InputNumber 
                  placeholder="e.g., 102" 
                  className="w-full"
                  min={1}
                  disabled={!!editingCombo}
                />
              </Form.Item>

              <Form.Item
                name="name"
                label="Combo Name"
                rules={[
                  { required: true, message: 'Please enter combo name' },
                  { min: 2, message: 'Minimum 2 characters' }
                ]}
              >
                <Input placeholder="e.g., New Year Gift Combo" />
              </Form.Item>

              <div className="grid grid-cols-2 gap-4">
                <Form.Item
                  name="price"
                  label="Price (₹)"
                  rules={[
                    { required: true, message: 'Please enter price' },
                    { type: 'number', min: 1, message: 'Minimum ₹1' }
                  ]}
                >
                  <InputNumber 
                    placeholder="4999" 
                    className="w-full"
                    min={1}
                  />
                </Form.Item>

                <Form.Item
                  name="originalPrice"
                  label="Original Price (₹)"
                  rules={[
                    { required: true, message: 'Please enter original price' },
                    { type: 'number', min: 1, message: 'Minimum ₹1' }
                  ]}
                >
                  <InputNumber 
                    placeholder="6999" 
                    className="w-full"
                    min={1}
                  />
                </Form.Item>
              </div>

              <Form.Item
                name="discount"
                label="Discount (%)"
                rules={[
                  { type: 'number', min: 0, max: 100, message: '0-100%' }
                ]}
              >
                <InputNumber 
                  placeholder="20" 
                  className="w-full"
                  min={0}
                  max={100}
                />
              </Form.Item>

              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: 'Please select category' }]}
              >
                <Select 
                  placeholder="Select category" 
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {categories.map(cat => (
                    <Option key={cat.slug} value={cat.slug}>
                      {cat.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* Right Column */}
            <Col span={12}>
              <Form.Item
                name="description"
                label="Description"
                rules={[{ required: true, message: 'Please enter description' }]}
              >
                <TextArea 
                  rows={4} 
                  placeholder="Enter combo description" 
                />
              </Form.Item>

              <div className="grid grid-cols-3 gap-4">
                <Form.Item
                  name="inStock"
                  label="In Stock"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>

                <Form.Item
                  name="allowCustomMessage"
                  label="Custom Message"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>

                <Form.Item
                  name="messageOnCard"
                  label="Message Card"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              </div>

              <Form.Item label="Main Image URL">
                <Input
                  value={mainImage}
                  onChange={handleMainImageChange}
                  placeholder="https://example.com/image.jpg"
                />
                {mainImage && (
                  <div className="mt-2">
                    <Image
                      src={mainImage}
                      alt="Preview"
                      width={100}
                      height={100}
                      className="rounded border"
                      fallback="https://via.placeholder.com/100"
                    />
                  </div>
                )}
              </Form.Item>
            </Col>
          </Row>

          {/* Product Selection */}
          <Divider orientation="left">
            <GiftOutlined /> Select Products for Combo
          </Divider>
          <Form.Item
            name="items"
            label="Products in Combo"
            rules={[{ required: true, message: 'Please select at least one product' }]}
          >
            <ProductSelector
              products={products}
              value={selectedItems}
              onChange={handleItemsChange}
            />
          </Form.Item>

          {/* Features */}
          <Divider orientation="left">
            <CheckCircleOutlined /> Combo Features
          </Divider>
          <Form.Item
            name="features"
            label="Features List"
            tooltip="List of features included in the combo"
          >
            <FeaturesArray />
          </Form.Item>

          {/* Additional Images */}
          <Divider orientation="left">
            <EyeOutlined /> Additional Images
          </Divider>
          <Form.Item label="Additional Images">
            <div className="space-y-2">
              {additionalImages.map((img, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={img}
                    onChange={(e) => handleImageChange(index, e.target.value)}
                    placeholder="https://example.com/image2.jpg"
                    className="flex-1"
                  />
                  <Button
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveImage(index)}
                  />
                  {img && (
                    <Image
                      src={img}
                      alt="Preview"
                      width={40}
                      height={40}
                      className="rounded border"
                    />
                  )}
                </div>
              ))}
              <Button type="dashed" onClick={handleAddImage} block icon={<PlusOutlined />}>
                Add Image URL
              </Button>
            </div>
          </Form.Item>

          {/* Brief Price Array */}
          <Divider orientation="left">
            <InfoCircleOutlined /> Price Options
          </Divider>
          <Form.Item
            name="brifPrize"
            label="Price Variations (Optional)"
            tooltip="Add different price options"
          >
            <BriefPriceArray />
          </Form.Item>

          {/* Form Actions */}
          <Form.Item className="mb-0 mt-6">
            <div className="flex justify-end gap-3">
              <Button onClick={onClose} size="large">
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                size="large"
                loading={loading}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                {editingCombo ? 'Update Combo' : 'Create Combo'}
              </Button>
            </div>
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

// ====================
// 8. MAIN COMPONENT
// ====================
const ComboManagement = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCombo, setEditingCombo] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // TanStack Query hooks
  const { data: categories = [] } = useCategories();
  const { data: products = [] } = useProducts();
  const { data: combos = [], isLoading, error } = useCombos();
  const deleteMutation = useDeleteCombo();

  // Filter combos
  const filteredCombos = combos.filter(combo => {
    const matchesSearch = 
      combo.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      combo.description?.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesCategory = 
      selectedCategory === 'all' || 
      combo.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Handle edit
  const handleEdit = (combo) => {
    setEditingCombo(combo);
    setModalOpen(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    await deleteMutation.mutateAsync(id);
  };

  // Reset form
  const handleModalClose = () => {
    setModalOpen(false);
    setEditingCombo(null);
  };

  // Calculate discount
  const calculateDiscount = (price, originalPrice) => {
    return Math.round((1 - price / originalPrice) * 100);
  };

  // Product detail view
  const ExpandedRow = ({ combo, allProducts }) => {
    const comboProducts = allProducts.filter(product => 
      combo.items?.includes(product.id)
    );

    return (
      <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
        <Row gutter={16}>
          <Col span={12}>
            <div className="space-y-3">
              <div>
                <strong className="block mb-1">Description:</strong>
                <p className="text-gray-700">{combo.description}</p>
              </div>
              
              <div>
                <strong className="block mb-1">Features:</strong>
                <ul className="list-disc pl-5 space-y-1">
                  {combo.features?.map((feature, idx) => (
                    <li key={idx} className="text-gray-700">{feature}</li>
                  ))}
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <strong className="block mb-1">Stock Status:</strong>
                  <Tag color={combo.inStock ? "green" : "red"} className="mt-1">
                    {combo.inStock ? "In Stock" : "Out of Stock"}
                  </Tag>
                </div>
                <div>
                  <strong className="block mb-1">Custom Message:</strong>
                  <Tag color={combo.allowCustomMessage ? "green" : "red"} className="mt-1">
                    {combo.allowCustomMessage ? "Allowed" : "Not Allowed"}
                  </Tag>
                </div>
              </div>
            </div>
          </Col>

          <Col span={12}>
            <div className="space-y-3">
              <div>
                <strong className="block mb-1">Products in Combo:</strong>
                {comboProducts.length > 0 ? (
                  <div className="space-y-2 mt-2">
                    {comboProducts.map(product => (
                      <div key={product.id} className="flex items-center gap-3 bg-white p-2 rounded border">
                        <Image
                          src={product.image}
                          alt={product.name}
                          width={40}
                          height={40}
                          className="rounded border object-cover"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{product.name}</div>
                          <div className="text-xs text-gray-500">ID: {product.id}</div>
                        </div>
                        <Tag color="blue">₹{product.price}</Tag>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-500 italic">No products selected</div>
                )}
              </div>

              {combo.brifPrize && combo.brifPrize.length > 0 && (
                <div>
                  <strong className="block mb-1">Price Options:</strong>
                  <div className="space-y-1 mt-2">
                    {combo.brifPrize.map((item, idx) => {
                      const [key, value] = Object.entries(item)[0];
                      return (
                        <div key={idx} className="flex justify-between bg-white p-2 rounded border">
                          <span>{key}</span>
                          <span className="font-bold text-green-600">₹{value}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* Additional Images */}
        {combo.images && combo.images.length > 0 && (
          <div className="mt-4">
            <strong className="block mb-2">Additional Images:</strong>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {combo.images.map((img, idx) => (
                <Image
                  key={idx}
                  src={img}
                  alt={`Combo image ${idx + 1}`}
                  width={80}
                  height={80}
                  className="rounded border object-cover"
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Table columns
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => a.id - b.id,
      render: (id) => <Badge count={id} style={{ backgroundColor: '#7c3aed' }} />,
    },
    {
      title: 'Combo',
      key: 'combo',
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <Image
            src={record.image}
            alt={record.name}
            width={60}
            height={60}
            className="rounded-lg border-2 border-purple-200 object-cover"
            fallback="https://via.placeholder.com/60"
          />
          <div>
            <div className="font-bold text-gray-800">{record.name}</div>
            <div className="text-xs text-gray-500 truncate max-w-xs">
              {record.items?.length || 0} products included
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Products',
      key: 'products',
      render: (_, record) => (
        <div className="text-center">
          <div className="text-lg font-bold text-purple-600">{record.items?.length || 0}</div>
          <div className="text-xs text-gray-500">Items</div>
        </div>
      ),
    },
    {
      title: 'Price',
      key: 'price',
      render: (_, record) => (
        <div>
          <div className="font-bold text-green-600 text-lg">₹{record.price}</div>
          <div className="flex items-center gap-2">
            <div className="text-sm text-gray-500 line-through">₹{record.originalPrice}</div>
            <Tag color="orange" className="text-xs">
              {calculateDiscount(record.price, record.originalPrice)}% OFF
            </Tag>
          </div>
        </div>
      ),
      sorter: (a, b) => a.price - b.price,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category) => (
        <Tag color="purple">{category}</Tag>
      ),
      filters: categories.map(cat => ({ text: cat.name, value: cat.slug })),
      onFilter: (value, record) => record.category === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
            className="bg-purple-500 hover:bg-purple-600"
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this combo?"
            description="Are you sure you want to delete this combo?"
            onConfirm={() => handleDelete(record.id || record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingOutlined className="text-4xl text-purple-500 mb-4" spin />
          <p className="text-gray-600">Loading combos...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-lg shadow-xl">
          <div className="text-red-500 text-5xl mb-4">🎁</div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Combos</h3>
          <p className="text-gray-600 mb-4">Failed to fetch combos from server</p>
          <Button 
            type="primary" 
            onClick={() => window.location.reload()}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
              <GiftOutlined className="text-white text-2xl" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">Combo Management</h1>
          </div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Create and manage product combos. Select products by ID to include in combos.
          </p>
        </div>

        {/* Stats */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={12} md={6}>
            <Card className="text-center shadow-lg hover:shadow-xl transition-all bg-gradient-to-r from-purple-50 to-white">
              <div className="text-2xl font-bold text-purple-600">{combos.length}</div>
              <div className="text-gray-600">Total Combos</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="text-center shadow-lg hover:shadow-xl transition-all bg-gradient-to-r from-pink-50 to-white">
              <div className="text-2xl font-bold text-pink-600">
                {combos.filter(c => c.inStock).length}
              </div>
              <div className="text-gray-600">In Stock</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="text-center shadow-lg hover:shadow-xl transition-all bg-gradient-to-r from-blue-50 to-white">
              <div className="text-2xl font-bold text-blue-600">
                {combos.reduce((sum, combo) => sum + (combo.items?.length || 0), 0)}
              </div>
              <div className="text-gray-600">Total Products</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="text-center shadow-lg hover:shadow-xl transition-all bg-gradient-to-r from-green-50 to-white">
              <div className="text-2xl font-bold text-green-600">
                {filteredCombos.length}
              </div>
              <div className="text-gray-600">Filtered</div>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card className="mb-6 shadow-xl rounded-xl border-0 bg-white">
          <Row gutter={16} align="middle">
            <Col xs={24} md={12}>
              <Input
                placeholder="Search combos by name or description..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                size="large"
                className="w-full rounded-lg"
                allowClear
              />
            </Col>
            <Col xs={12} md={6}>
              <Select
                value={selectedCategory}
                onChange={setSelectedCategory}
                size="large"
                className="w-full rounded-lg"
              >
                <Option value="all">All Categories</Option>
                {categories.map(cat => (
                  <Option key={cat.slug} value={cat.slug}>
                    {cat.name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} md={6}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingCombo(null);
                  setModalOpen(true);
                }}
                size="large"
                className="w-full rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg"
              >
                Create Combo
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Combos Table */}
        <Card className="shadow-2xl rounded-2xl overflow-hidden border-0 bg-white">
          <div className="p-6 border-b bg-gradient-to-r from-purple-50 to-pink-50">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">All Combos</h2>
                <p className="text-gray-600">
                  Showing {filteredCombos.length} of {combos.length} combos
                </p>
              </div>
              <Button 
                type="text" 
                onClick={() => {
                  setSearchText('');
                  setSelectedCategory('all');
                }}
                icon={<SearchOutlined />}
              >
                Clear Filters
              </Button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <Table
              columns={columns}
              dataSource={filteredCombos}
              rowKey={record => record.id || record._id}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `${total} combos`,
                className: 'px-6'
              }}
              expandable={{
                expandedRowRender: (record) => (
                  <ExpandedRow combo={record} allProducts={products} />
                ),
                rowExpandable: (record) => true,
              }}
              className="combo-table"
              size="middle"
              scroll={{ x: true }}
            />
          </div>
        </Card>

        {/* Form Modal */}
        <ComboForm
          open={modalOpen}
          onClose={handleModalClose}
          editingCombo={editingCombo}
          categories={categories}
          products={products}
        />
      </div>
    </div>
  );
};

 
export default ComboManagement;